import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ChatService } from '../../services/chat.service';
import { ModalService } from '../../services/modal.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { TenantService } from '../../../../shared/services/tenant.service';
import { ChatHistoryMessage, ChatHistoryItem, mapChatHistoryMessage } from '../../interfaces/chat-history-response.interface';
import { GetChatHistoryByChatIdModel } from '../../interfaces/get-chat-history-by-chat-id.interface';
import { UpdateChatResponse } from '../../interfaces/update-chat.interface';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.css']
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isCollapsed = false;
  @Input() isCollectionsSelected = false;
  @Input() isFeedbackSelected = false;
  @Input() isMailboxSelected = false;
  @Input() isVisualizationsSelected = false;
  @Input() isInferencesSelected = false;
  @Input() isClustersSelected = false;
  @Input() isLeaveSelected = false;
  @Input() isAgentHooksSelected = false;
  @Input() canViewAgentHooks = false;
  @Input() canViewCollections = false;
  @Input() canViewFeedback = false;
  @Input() canViewMailbox = false;
  @Input() canViewVisualizations = false;
  @Input() canViewInferences = false;
  @Output() toggleSidebar = new EventEmitter<void>();
  @Output() collectionsClicked = new EventEmitter<void>();
  @Output() feedbackClicked = new EventEmitter<void>();
  @Output() mailboxClicked = new EventEmitter<void>();
  @Output() visualizationsClicked = new EventEmitter<void>();
  @Output() inferencesClicked = new EventEmitter<void>();
  @Output() clustersClicked = new EventEmitter<void>();
  @Output() leaveClicked = new EventEmitter<void>();
  @Output() agentHooksClicked = new EventEmitter<void>();
  @Output() promptsClicked = new EventEmitter<void>();
  @Output() newChatClicked = new EventEmitter<void>();
  @Output() chatHistoryItemClicked = new EventEmitter<{ messages: ChatHistoryMessage[]; chatId: string; chatLabel?: string; isLoading?: boolean }>();
  @Output() chatDeleted = new EventEmitter<string>();

  chatHistory: ChatHistoryMessage[] = [];
  chatHistoryItems: ChatHistoryItem[] = [];
  last7DaysChats: ChatHistoryItem[] = [];
  earlierChats: ChatHistoryItem[] = [];
  selectedChatHistoryId: string | null = null;
  isLoadingHistory = false;
  isLoadingChatSwitch = false;
  username: string = '';
  showUserMenu = false;
  savingChatId: string | null = null;
  private subscriptions: Subscription[] = [];
  private activeChatId: string | null = null;
  private shouldOpenMostRecentOnStartup = false;
  private startupSelectionResolved = false;

  constructor(
    private chatService: ChatService,
    private modalService: ModalService,
    private authService: AuthService,
    private tenantService: TenantService
  ) { }

  ngOnInit(): void {
    this.shouldOpenMostRecentOnStartup = sessionStorage.getItem('open_most_recent_chat') === 'true';
    sessionStorage.removeItem('open_most_recent_chat');

    // Load active chat id first so we can highlight it once history arrives
    const activeSub = this.chatService.getActiveChatHistory().subscribe({
      next: (res: any) => {
        if (res && res.isSuccess && res.data && res.data.chatId) {
          this.activeChatId = res.data.chatId;
        }
      },
      error: () => { /* ignore */ }
    });
    this.subscriptions.push(activeSub);

    this.loadChatHistoryFromServer();
    const userInfoString = localStorage.getItem('user_info');
    if (userInfoString) {
      try {
        const userInfo = JSON.parse(userInfoString);
        this.username = userInfo.username || '';
      } catch (error) {
        console.error('Error parsing user_info from localStorage:', error);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private groupChatHistoriesByDate(chatHistories: ChatHistoryItem[]): void {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));

    this.last7DaysChats = [];
    this.earlierChats = [];

    chatHistories.forEach(chat => {
      // Extract createdDate from the first message in messagesJson
      let chatDate: Date;

      try {
        const messages = JSON.parse(chat.messagesJson);
        if (messages && messages.length > 0 && messages[0].CreatedDate) {
          chatDate = new Date(messages[0].CreatedDate);
        } else {
          // Fallback to current date if no date found
          chatDate = new Date();
        }
      } catch (error) {
        console.error('Error parsing messagesJson for date:', error);
        chatDate = new Date();
      }

      const isWithin7Days = chatDate >= sevenDaysAgo;

      if (isWithin7Days) {
        this.last7DaysChats.push(chat);
      } else {
        this.earlierChats.push(chat);
      }
    });

    // Sort both arrays by date (newest first)
    this.last7DaysChats.sort((a, b) => {
      const dateA = this.getChatDate(a);
      const dateB = this.getChatDate(b);
      return dateB.getTime() - dateA.getTime();
    });
    this.earlierChats.sort((a, b) => {
      const dateA = this.getChatDate(a);
      const dateB = this.getChatDate(b);
      return dateB.getTime() - dateA.getTime();
    });
  }

  private getChatDate(chat: ChatHistoryItem): Date {
    try {
      const messages = JSON.parse(chat.messagesJson);
      if (messages && messages.length > 0 && messages[0].CreatedDate) {
        return new Date(messages[0].CreatedDate);
      }
    } catch (error) {
      console.error('Error parsing messagesJson for sorting:', error);
    }
    return new Date();
  }

  private loadChatHistoryFromServer(): void {
    this.isLoadingHistory = true;
    const historySubscription = this.chatService.getChatHistory().subscribe({
      next: (response) => {
        this.isLoadingHistory = false;
        if (response && response.isSuccess && response.data && response.data.chatHistories && response.data.chatHistories.length > 0) {
          this.chatHistoryItems = response.data.chatHistories;
          // Group chat histories by date
          this.groupChatHistoriesByDate(response.data.chatHistories);
          // Parse the first chat history for backward compatibility
          if (response.data.chatHistories.length > 0) {
            this.parseFirstChatHistory(response.data.chatHistories[0]);
          }

          // Select active chat if available
          if (this.activeChatId) {
            const existsInRecent = this.last7DaysChats.some(c => c.id === this.activeChatId);
            const existsInEarlier = this.earlierChats.some(c => c.id === this.activeChatId);
            if (existsInRecent || existsInEarlier) {
              this.selectedChatHistoryId = this.activeChatId;
            }
          }

          this.resolveStartupSelection();
        } else {
          this.resolveStartupSelection();
        }
      },
      error: (error) => {
        this.isLoadingHistory = false;
        console.error('Error loading chat history:', error);
        this.resolveStartupSelection();
      }
    });
    this.subscriptions.push(historySubscription);
  }

  private resolveStartupSelection(): void {
    if (this.startupSelectionResolved) {
      return;
    }

    this.startupSelectionResolved = true;

    // Only auto-open chat right after successful login.
    if (!this.shouldOpenMostRecentOnStartup) {
      this.onNewChatClick();
      return;
    }

    const mostRecentChat = this.getMostRecentChatItem();
    if (mostRecentChat) {
      this.onChatHistoryItemClick(mostRecentChat);
      return;
    }

    this.onNewChatClick();
  }

  private getMostRecentChatItem(): ChatHistoryItem | null {
    if (this.last7DaysChats.length > 0) {
      return this.last7DaysChats[0];
    }

    if (this.earlierChats.length > 0) {
      return this.earlierChats[0];
    }

    return null;
  }

  private parseFirstChatHistory(chatHistoryItem: ChatHistoryItem): void {
    try {
      const messages = JSON.parse(chatHistoryItem.messagesJson);
      this.chatHistory = messages.map((msg: any) => mapChatHistoryMessage(msg));
    } catch (error) {
      console.error('Error parsing chat history messages:', error);
      this.chatHistory = [];
    }
  }

  // Method to add a saved chat from UpdateChat API response
  addSavedChat(chatId: string, label: string): void {
    // Instead of adding to savedChats, reload the chat history to get proper date grouping
    this.loadChatHistoryFromServer();
    this.selectedChatHistoryId=chatId;
  }

  onNewChatClick(): void {
    // Clear any selected chat history item
    this.selectedChatHistoryId = null;
    this.activeChatId=null;
    this.newChatClicked.emit();
  }


  onChatHistoryItemClick(chatHistoryItem: ChatHistoryItem): void {

      // If clicking on already selected chat, emit event to show it (useful when returning from Collections)
      if (this.selectedChatHistoryId === chatHistoryItem.id) {
        // Emit with empty messages array - chat-layout will just hide Collections, chat state is preserved
        this.chatHistoryItemClicked.emit({
          messages: [],
          chatId: chatHistoryItem.id,
          chatLabel: chatHistoryItem.chatLabel,
          isLoading: false
        });
        return;
      }
    
    this.selectedChatHistoryId = chatHistoryItem.id; // Set selected item

    // Show loading state
    this.isLoadingChatSwitch = true;
    this.chatHistoryItemClicked.emit({
      messages: [],
      chatId: chatHistoryItem.id,
      chatLabel: chatHistoryItem.chatLabel,
      isLoading: true
    });

    // Call API to get specific chat history
    const getChatHistoryByChatIdModel: GetChatHistoryByChatIdModel = {
      chatId: chatHistoryItem.id
    };

    const getChatHistorySubscription = this.chatService.getChatHistoryByChatId(getChatHistoryByChatIdModel).subscribe({
      next: (response) => {
        this.isLoadingChatSwitch = false;
        if (response && response.isSuccess && response.data) {
          // Parse the messages and emit them
          try {
            const messages = JSON.parse(response.data.messagesJson);
            const chatHistoryMessages: ChatHistoryMessage[] = messages.map((msg: any) => mapChatHistoryMessage(msg));
            this.chatHistoryItemClicked.emit({
              messages: chatHistoryMessages,
              chatId: chatHistoryItem.id,
              chatLabel: chatHistoryItem.chatLabel,
              isLoading: false
            });
          } catch (error) {
            console.error('Error parsing chat history messages:', error);
            this.isLoadingChatSwitch = false;
          }
        }
      },
      error: (error) => {
        console.error('Error getting chat history by ID:', error);
        this.isLoadingChatSwitch = false;
        this.chatHistoryItemClicked.emit({
          messages: [],
          chatId: chatHistoryItem.id,
          chatLabel: chatHistoryItem.chatLabel,
          isLoading: false
        });
      }
    });

    this.subscriptions.push(getChatHistorySubscription);
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onDeleteChat(event: Event, chatId: string): void {
    event.stopPropagation();

    const chatItem = [...this.last7DaysChats, ...this.earlierChats].find(chat => chat.id === chatId);
    const chatTitle = chatItem?.chatLabel;

    this.modalService.openDeleteChatModal(chatTitle).then((confirmed) => {
      if (confirmed) {
        const deleteSubscription = this.chatService.deleteChat(chatId).subscribe({
          next: (response) => {
            this.last7DaysChats = this.last7DaysChats.filter(chat => chat.id !== chatId);
            this.earlierChats = this.earlierChats.filter(chat => chat.id !== chatId);

            if (this.selectedChatHistoryId === chatId) {
              this.selectedChatHistoryId = null;

              this.chatDeleted.emit(chatId);
            }

              this.tenantService.navigateToPrimaryChat();
          },
          error: (error) => {
            console.error('Error deleting chat:', error);
            alert('Failed to delete chat. Please try again.');
          }
        });

        this.subscriptions.push(deleteSubscription);
      }
    }).catch(() => {

    });
  }

  onEditChat(event: Event, chatHistoryItem: ChatHistoryItem): void {
    event.stopPropagation();
    this.modalService.openEditConversationNameModal(chatHistoryItem.chatLabel).then((newName: string) => {
      this.saveChatRename(chatHistoryItem, newName);
    }).catch(() => undefined);
  }

  saveChatRename(chatHistoryItem: ChatHistoryItem, newLabel: string): void {
    if (this.savingChatId === chatHistoryItem.id) {
      return;
    }

    const nextLabel = newLabel.trim();
    const currentLabel = chatHistoryItem.chatLabel.trim();

    if (!nextLabel) {
      return;
    }

    if (nextLabel === currentLabel) {
      return;
    }

    this.savingChatId = chatHistoryItem.id;

    const renameSubscription = this.chatService.updateChat({
      chatId: chatHistoryItem.id,
      chatLabel: nextLabel
    }).subscribe({
      next: (response: UpdateChatResponse) => {
        const savedLabel = response?.data?.chatLabel || nextLabel;
        chatHistoryItem.chatLabel = savedLabel;
        this.updateChatLabelInLists(chatHistoryItem.id, savedLabel);
        this.savingChatId = null;
      },
      error: (error) => {
        console.error('Error renaming chat:', error);
        this.savingChatId = null;
        alert('Failed to rename chat. Please try again.');
      }
    });

    this.subscriptions.push(renameSubscription);
  }

  private updateChatLabelInLists(chatId: string, chatLabel: string): void {
    const updateLabel = (chat: ChatHistoryItem) => {
      if (chat.id === chatId) {
        chat.chatLabel = chatLabel;
      }
    };

    this.chatHistoryItems.forEach(updateLabel);
    this.last7DaysChats.forEach(updateLabel);
    this.earlierChats.forEach(updateLabel);
  }

  onCollectionsClick(): void {
    // Don't clear selectedChatHistoryId to preserve active chat state
    if (this.canViewCollections) {
      this.collectionsClicked.emit();
    }
  }

  onFeedbackClick(): void {
    if (this.canViewFeedback) {
      this.feedbackClicked.emit();
    }
  }

  onMailboxClick(): void {
    if (this.canViewMailbox) {
      this.mailboxClicked.emit();
    }
  }

  onVisualizationsClick(): void {
    if (this.canViewVisualizations) {
      this.visualizationsClicked.emit();
    }
  }

  onInferencesClick(): void {
    if (this.canViewInferences) {
      this.inferencesClicked.emit();
    }
  }

  onClustersClick(): void {
    if (this.canViewInferences) {
      this.clustersClicked.emit();
    }
  }

  onLeaveClick(): void {
    this.leaveClicked.emit();
  }

  onAgentHooksClick(): void {
    this.agentHooksClicked.emit();
  }

  onPromptsClick(): void {
    this.promptsClicked.emit();
  }

  toggleUserMenu(): void {
    this.showUserMenu = !this.showUserMenu;
  }

  onChangePasswordClick(): void {
    this.showUserMenu = false;
    this.modalService.openChangePasswordModal().then(() => {

    }).catch(() => {

    });
  }

  onLogoutClick(): void {
    this.showUserMenu = false;
    this.authService.logout().subscribe({
      next: () => {
      },
      error: (error) => {
        console.error('Logout error:', error);
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const menuContainer = target.closest('.user-menu-container');

    if (!menuContainer && this.showUserMenu) {
      this.showUserMenu = false;
    }
  }
  capitalizeFirst(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
}
